import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  DocumentTextIcon,
  DocumentIcon,
  VideoCameraIcon,
  CodeBracketIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BookOpenIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import MarkdownViewer from '../Education/MarkdownViewer';

const CONTENT_TYPE_ICONS = {
  markdown: DocumentTextIcon,
  document: DocumentIcon,
  video: VideoCameraIcon,
  iframe: CodeBracketIcon,
};

const CONTENT_TYPE_LABELS = {
  markdown: 'Article',
  document: 'Document',
  video: 'Video',
  iframe: 'Interactive Content',
};

function VideoEmbed({ url }) {
  // Extract video ID and platform
  const getVideoEmbedUrl = (videoUrl) => {
    // YouTube
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const videoId = videoUrl.includes('youtu.be')
        ? videoUrl.split('youtu.be/')[1]?.split('?')[0]
        : videoUrl.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // Vimeo
    if (videoUrl.includes('vimeo.com')) {
      const videoId = videoUrl.split('vimeo.com/')[1]?.split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }

    // Direct video URL
    return videoUrl;
  };

  const embedUrl = getVideoEmbedUrl(url);
  const isDirectVideo = !embedUrl.includes('youtube.com') && !embedUrl.includes('vimeo.com');

  if (isDirectVideo) {
    return (
      <video src={embedUrl} controls className="w-full rounded-lg" style={{ maxHeight: '400px' }}>
        Your browser does not support the video tag.
      </video>
    );
  }

  return (
    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
      <iframe
        src={embedUrl}
        className="absolute top-0 left-0 w-full h-full rounded-lg"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Video embed"
      />
    </div>
  );
}

function IFrameEmbed({ content }) {
  return <div className="w-full" dangerouslySetInnerHTML={{ __html: content }} />;
}

// Dedicated modal for displaying markdown content - fully scrollable and viewport-aware
// Uses React Portal to render outside parent DOM hierarchy to avoid z-index stacking context issues
const MarkdownModal = ({ isOpen, onClose, item }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !item) {
    return null;
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleBackdropWheel = (e) => {
    // Prevent background scrolling
    if (e.target === e.currentTarget) {
      e.stopPropagation();
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-start justify-center p-4 overflow-y-auto"
      onClick={handleBackdropClick}
      onWheel={handleBackdropWheel}
      onTouchMove={handleBackdropWheel}
      style={{ 
        WebkitOverflowScrolling: 'touch',
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.6)'
      }}
      data-markdown-modal="true"
    >
      <div 
        className="relative bg-white w-full max-w-4xl rounded-xl shadow-xl border border-gray-200 flex flex-col my-4"
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{item.title}</h3>
            {item.description && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
            aria-label="Close"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div 
          className="flex-1 min-h-0 overflow-y-auto p-6 bg-gray-50"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <MarkdownViewer body={item.md_body} />
          </div>
        </div>
      </div>
    </div>
  );

  // Render the modal as a portal directly to document.body
  // This escapes any parent stacking contexts (like TaskDetailModal)
  return ReactDOM.createPortal(modalContent, document.body);
};

function ContentItem({ item }) {
  const [showMarkdownModal, setShowMarkdownModal] = useState(false);
  const Icon = CONTENT_TYPE_ICONS[item.content_type] || DocumentTextIcon;
  const typeLabel = CONTENT_TYPE_LABELS[item.content_type] || 'Content';

  const handleClick = (e) => {
    // Prevent event from bubbling to parent handlers
    e.preventDefault();
    e.stopPropagation();
    
    if (item.content_type === 'markdown' && item.md_body) {
      // Open markdown in modal
      setShowMarkdownModal(true);
    } else {
      // For other types, open in new tab or handle differently
      if (item.asset_url) {
        window.open(item.asset_url, '_blank');
      }
    }
  };

  return (
    <>
      {/* Header - always visible, clickable */}
      <button
        type="button"
        onClick={handleClick}
        className="w-full flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors text-left cursor-pointer"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-lg border border-gray-300 bg-gray-100 flex items-center justify-center">
          <Icon className="w-5 h-5 text-gray-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h4 className="font-semibold text-gray-900">{item.title}</h4>
            <span className="px-2 py-0.5 text-xs rounded-full border border-gray-300 bg-white text-gray-700">
              {typeLabel}
            </span>
            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
              {item.locale || 'EN'}
            </span>
          </div>
          {item.description && (
            <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
          )}
        </div>
        <div className="flex-shrink-0">
          <ChevronDownIcon className="w-5 h-5 text-gray-500" />
        </div>
      </button>

      {/* Markdown Modal */}
      {item.content_type === 'markdown' && (
        <MarkdownModal
          isOpen={showMarkdownModal}
          onClose={() => setShowMarkdownModal(false)}
          item={item}
        />
      )}
    </>
  );
}

const EducationalContentViewer = ({ content = [], userLocale = 'EN', className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(true); // Expanded by default

  console.log('[EducationalContentViewer] Received props:', { 
    contentLength: content.length, 
    userLocale, 
    content: content.slice(0, 2) 
  });

  // Filter content by user locale (case-insensitive), fallback to EN if no content in user's locale
  const normalizedUserLocale = (userLocale || 'EN').toUpperCase();
  const filteredContent = content.filter((item) => {
    const itemLocale = (item.locale || 'EN').toUpperCase();
    return itemLocale === normalizedUserLocale;
  });
  console.log('[EducationalContentViewer] Filtered content:', filteredContent.length, 'comparing', normalizedUserLocale);
  
  const displayContent =
    filteredContent.length > 0
      ? filteredContent
      : content.filter((item) => (item.locale || 'EN').toUpperCase() === 'EN');
  console.log('[EducationalContentViewer] Display content:', displayContent.length);

  // Sort by sortOrder
  const sortedContent = [...displayContent].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  console.log('[EducationalContentViewer] Sorted content:', sortedContent.length);

  if (sortedContent.length === 0) {
    console.log('[EducationalContentViewer] No content to display, returning null');
    return null;
  }

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
        >
          <BookOpenIcon className="w-5 h-5" />
          Learning Resources
          <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
            {sortedContent.length}
          </span>
          {isExpanded ? (
            <ChevronUpIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-3 border-l-2 border-primary/20 pl-4">
          {sortedContent.map((item) => (
            <ContentItem key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
};

export default EducationalContentViewer;

